import throttle from 'lodash/throttle';
import debounce from 'lodash/debounce';

import classnames from 'classnames/bind';
import EmailValidator from 'email-validator';
import PropTypes from 'prop-types';
import React from 'react';

import InputBox from 'presentation/components/InputBox';
import overrideStyle from 'lib/overrideStyle';
import SubmitButtonContainer from 'presentation/containers/SubmitButtonContainer';
import { TypingState } from 'enums/session';

import styles from 'presentation/components/styles/Composition.css';
let cx = classnames.bind(styles);

// Typing event is throttled to 1/5sec, stop typing event debounced every start typing call
// Stop typing timeout must be at least 500ms longer to allow time for next typing update
const START_TYPING_INTERVAL = 5000;
const STOP_TYPING_TIMEOUT = 5500;

class Composition extends React.Component {
  constructor(props) {
    super(props);
    this.state = { isValidEmail: true };

    this.handleInputChange = this.handleInputChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleKeyPress = this.handleKeyPress.bind(this);
  }

  componentDidMount() {
    this.input.focus();
  }

  componentWillMount() {
    this.handleActiveTyping = throttle(this.handleTypingStart, START_TYPING_INTERVAL, { trailing: false });
    this.handleTypingTimeout = debounce(this.handleTypingStop, STOP_TYPING_TIMEOUT);
  }

  handleInputChange(ev) {
    this.props.onChange(ev.target.value);
    this.handleActiveTyping();
  }

  handleKeyPress(ev) {
    let charCode = ev.which || ev.keyCode;

    if (charCode === 13 && !ev.shiftKey) {
      this.handleSubmit();
      ev.stopPropagation();
      ev.preventDefault();
      return false;
    }
  }

  handleSubmit() {
    // dont send unless there is at least one non-whitespace character and the chat client is connected
    if (!/\S+/.test(this.props.compositionDraft) || !this.props.isChatClientConnected) {
      return;
    }
    this.handleTypingStop();

    if (this.props.isOnboarding) {
      const email = this.props.compositionDraft.trim();
      const isValidEmail = this.validateEmail(email);
      this.setState({ isValidEmail });
      isValidEmail && this.props.onOnboard(email);
    } else {
      this.props.onSubmit(this.props.compositionDraft);
    }

    this.input.focus();
  }

  handleTypingStart() {
    this.props.onCustomerTypingStateUpdate(TypingState.STARTED);
    this.handleTypingTimeout();
  }

  handleTypingStop() {
    this.props.onCustomerTypingStateUpdate(TypingState.STOPPED);
  }

  validateEmail(email) {
    return EmailValidator.validate(email);
  }

  render() {
    // classname only applies when there there is content in the inputBox
    let submitClasses = cx({ chatSubmitArrow_customColors: /\S+/.test(this.props.compositionDraft) });
    let compositionInputBoxClasses = cx(styles.composition_inputBox, 'composition_inputBox');
    let compositionClassnames = cx(styles.composition, { composition_selfService: this.props.isSelfService });
    let prompt = this.props.isOnboarding ? 'Enter your email' : this.props.prompt;

    return (
      <div className={compositionClassnames}>
        <div className={styles.composition_wrapper}>
          <pre className={styles.composition_spacer}>
            {this.props.compositionDraft}
            <br />
          </pre>
          {this.props.isOnboarding && !this.state.isValidEmail ? (
            <CompositionEmailError>Please enter a valid email address</CompositionEmailError>
          ) : null}
          <InputBox
            aria-label={this.props.isOnboarding ? 'emailInput' : 'messageInput'}
            className={compositionInputBoxClasses}
            data-aid="gladlyChatCompositionInput"
            data-gramm_editor="false"
            onChange={this.handleInputChange}
            onKeyPress={this.handleKeyPress}
            prompt={prompt}
            ref={input => (this.input = input)}
            role="form"
            value={this.props.compositionDraft}
          />
        </div>
        <SubmitButtonContainer
          aria-label="submitButton"
          role="button"
          className={submitClasses}
          onClick={this.handleSubmit}
          onKeyDown={this.handleKeyPress}
          data-aid="gladlyChatCompositionSubmit"
        />
      </div>
    );
  }
}

Composition.propTypes = {
  prompt: PropTypes.string.isRequired,
  isChatClientConnected: PropTypes.bool.isRequired,
  compositionDraft: PropTypes.string.isRequired,
  isOnboarding: PropTypes.bool,
  isSelfService: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  onCustomerTypingStateUpdate: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onOnboard: PropTypes.func.isRequired,
};

export default Composition;

const CompositionEmailError = overrideStyle('span')`
  color: #D2220E;
  font-family: ${p => p.theme.fontLibrary.secondary};
  font-size: 11px;
  left: 20px;
  position: absolute;
  top: 10px;
  z-index: 1;
`;
